/** Unused seats per SKU — the board's waste ranking (L1).
 *
 *  Comparing magnitudes across named categories is a sorted horizontal bar
 *  (dashboard design rules §2), not the full SKU table that used to sit here.
 *  One series means one colour for every bar (§3.1): the ranking is carried by
 *  bar length and sort order, so tinting each bar darker-where-bigger would
 *  spend the colour channel on information the chart already shows.
 *
 *  The full SKU grid is still one click away — "View all SKUs" opens it as an
 *  L2 drawer, URL-addressable so the view survives a refresh or a shared link.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { Button, EmptyState } from "@norican/react";

import { ChartCard, DetailDrawer } from "@/components/board";
import type { LicenseSkuRow } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { formatMoney, formatPct, formatSeats } from "./licence-format";
import { SkuWasteGrid } from "./waste-table";

/** `?skus=all` opens the full SKU grid. */
const ALL_SKUS_PARAM = "skus";
const ALL_SKUS_ID = "all";

/** How many SKUs the ranking shows — the rest are behind View all SKUs. */
const TOP_LIMIT = 10;

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

/** One series, one colour — never a ramp over nominal categories (§3.1). */
const WASTE_CONFIG: ChartConfig = {
  unused: { label: "Unused seats", color: "var(--norican-accent)" },
};

interface SkuWasteChartProps {
  rows: LicenseSkuRow[];
  currency: string;
  pricesConfigured: boolean;
  generatedAt: string | null | undefined;
}

export function SkuWasteChart({
  rows,
  currency,
  pricesConfigured,
  generatedAt,
}: SkuWasteChartProps) {
  const allSkus = useDrawerParam(ALL_SKUS_PARAM);

  const wasteful = rows.filter((row) => row.unused > 0);
  const ranked = [...wasteful].sort((a, b) => b.unused - a.unused);
  const barRows = ranked.slice(0, TOP_LIMIT);

  return (
    <>
      <ChartCard
        title="Unused seats by SKU"
        caption={
          ranked.length > TOP_LIMIT
            ? `Top ${TOP_LIMIT} of ${ranked.length} SKUs with unused seats`
            : "SKUs with unused seats, most wasteful first"
        }
        since={generatedAt}
        staleAfterMs={STALE_AFTER_MS}
        actions={
          <Button size="sm" variant="ghost" onClick={() => allSkus.open(ALL_SKUS_ID)}>
            View all SKUs
          </Button>
        }
        tableTwin={{
          id: "sku-waste",
          ariaLabel: "Unused seats per SKU",
          data: ranked,
          getRowId: (row: LicenseSkuRow) => row.sku_id,
          columns: [
            { id: "name", header: "SKU", accessor: (row) => row.name },
            {
              id: "unused",
              header: "Unused",
              accessor: (row) => row.unused,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatSeats(row.unused)}</span>,
            },
            {
              id: "utilisation",
              header: "Utilisation",
              accessor: (row) => row.utilisation_pct,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatPct(row.utilisation_pct)}</span>,
            },
            {
              id: "cost",
              header: "Idle cost / month",
              accessor: (row) =>
                row.monthly_unit_cost == null ? null : row.monthly_unit_cost * row.unused,
              align: "end",
              cell: (row) => (
                <span className="tabular-nums">
                  {(row.monthly_unit_cost == null
                    ? null
                    : formatMoney(row.monthly_unit_cost * row.unused, currency)) ?? "—"}
                </span>
              ),
            },
          ],
        }}
      >
        {barRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No unused seats"
            description="Every purchased seat on the reported SKUs is assigned."
          />
        ) : (
          <Chart.Container
            config={WASTE_CONFIG}
            className="h-72"
            aria-label={`Unused seats for the ${barRows.length} most wasteful SKUs`}
          >
            <Chart.BarChart
              data={barRows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="name" width={160} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    formatter={(value) => `${formatSeats(Number(value))} unused`}
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="unused"
                fill="var(--color-unused)"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={() => allSkus.open(ALL_SKUS_ID)}
              />
            </Chart.BarChart>
          </Chart.Container>
        )}
      </ChartCard>

      <DetailDrawer
        open={allSkus.value === ALL_SKUS_ID}
        onClose={allSkus.close}
        title="All licence SKUs"
        breadcrumbs={[{ label: "Licences", to: "/licences" }]}
      >
        <SkuWasteGrid rows={rows} currency={currency} pricesConfigured={pricesConfigured} />
      </DetailDrawer>
    </>
  );
}
